import React, { useState, useEffect } from 'react';
import { Crown, X, Target, Trophy, Brain, Zap, Star, Award, Share2, Heart, Calculator, Lightbulb, Smile, FlaskRound as Flask } from 'lucide-react';
import { getTodayTopLikes, Like } from '../utils/likes';
import { supabase } from '../utils/supabase';
import challenges from '../data/dailyChallenges.json';
import LikeButton from './LikeButton';
import AchievementItem from './AchievementItem';
import { useAuth } from '../hooks/useAuth';
import { getClientId } from '../utils/clientId';
import AchievementsModal from './AchievementsModal';
import type { Achievement, UserAchievement } from '../types/achievements';

const TopAchievement = () => {
  const { session } = useAuth();
  const [topLikes, setTopLikes] = useState<Like[]>([]);
  const [selectedItem, setSelectedItem] = useState<Like | null>(null);
  const [showAchievements, setShowAchievements] = useState(false);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [userStats, setUserStats] = useState({
    totalPoints: 0,
    totalLikes: 0,
    totalChallenges: 0,
    currentStreak: 0,
    level: 1,
    progress: 0
  });

  useEffect(() => {
    const handleChallengeCompleted = (event: CustomEvent) => {
      const { stats } = event.detail;
      setUserStats(prevStats => ({
        ...prevStats,
        totalPoints: stats.total_points,
        totalChallenges: stats.total_challenges,
        currentStreak: stats.current_streak,
        level: Math.floor(stats.total_points / 100) + 1,
        progress: (stats.total_points % 100) / 100 * 100
      }));
    };

    window.addEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
    return () => {
      window.removeEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
    };
  }, []);

  useEffect(() => {
    const updateStats = async () => {
      try {
        const clientId = getClientId();
        
        const { data: userStatsData } = await supabase
          .from('user_stats')
          .select('*')
          .eq('client_id', clientId)
          .single();

        if (userStatsData) {
          const level = Math.floor((userStatsData.total_points || 0) / 100) + 1;
          const progress = ((userStatsData.total_points || 0) % 100) / 100 * 100;

          setUserStats({
            totalPoints: userStatsData.total_points || 0,
            totalLikes: userStatsData.total_likes || 0,
            totalChallenges: userStatsData.total_challenges || 0,
            currentStreak: userStatsData.current_streak || 0,
            level,
            progress
          });
        }

        const [achievementsData, userAchievementsData] = await Promise.all([
          supabase.from('achievements').select('*').order('points', { ascending: true }),
          supabase.from('user_achievements').select('*, achievement:achievements(*)').eq('client_id', clientId)
        ]);

        if (achievementsData.data) setAchievements(achievementsData.data);
        if (userAchievementsData.data) setUserAchievements(userAchievementsData.data);

        const likes = await getTodayTopLikes();
        setTopLikes(likes || []);
      } catch (error) {
        console.error('Error updating stats:', error);
      }
    };

    updateStats();
    const interval = setInterval(updateStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleShare = async () => {
    try {
      const text = `Level ${userStats.level} Explorer with ${userStats.totalPoints} points and ${userAchievements.length} achievements! Check out The Most Useless Website!`;
      
      if (navigator.share) {
        await navigator.share({
          title: 'My Useless Achievements',
          text,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(`${text}\n${window.location.href}`);
        alert('Profile link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const renderDetailModal = (item: Like) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black/40 rounded-2xl shadow-xl p-6 max-w-lg w-full relative border border-white/10">
        <button 
          onClick={() => setSelectedItem(null)}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition"
        >
          <X className="h-6 w-6" />
        </button>
        <AchievementItem 
          achievement={item}
          showDetailedView={true}
          isTopAchievement={item === topLikes[0]}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Main Stats Card */}
      <div className="card p-6 hover-lift group">
        {/* Header with Level Progress */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Crown className="h-8 w-8 text-yellow-400" />
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-bold gradient-text">Level {userStats.level} Explorer</h2>
                <button
                  onClick={handleShare}
                  className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition"
                >
                  <Share2 className="h-4 w-4 text-gray-400" />
                </button>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <div className="w-32 h-1.5 bg-black/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full transition-all duration-1000"
                    style={{ width: `${userStats.progress}%` }}
                  />
                </div>
                <span>{Math.round(userStats.progress)}% to Level {userStats.level + 1}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex space-x-8">
            <div className="flex items-center space-x-2 text-yellow-400">
              <Star className="h-5 w-5" />
              <span className="text-lg font-bold">{userStats.totalPoints}</span>
            </div>
            <div className="flex items-center space-x-2 text-purple-400">
              <Trophy className="h-5 w-5" />
              <span className="text-lg font-bold">{userAchievements.length}/{achievements.length}</span>
            </div>
            <div className="flex items-center space-x-2 text-green-400">
              <Target className="h-5 w-5" />
              <span className="text-lg font-bold">{userStats.totalChallenges}</span>
            </div>
            <div className="flex items-center space-x-2 text-orange-400">
              <Zap className="h-5 w-5" />
              <span className="text-lg font-bold">{userStats.currentStreak}</span>
            </div>
          </div>
        </div>

        {/* Points System */}
        <div className="mb-6 bg-black/20 rounded-lg p-4">
          <h3 className="text-sm font-semibold gradient-text mb-3 flex items-center space-x-2">
            <Star className="h-4 w-4" />
            <span>How to Earn Points</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-3 text-gray-300">
                <Heart className="h-4 w-4 text-red-400" />
                <span>Like any content: <strong>10 points</strong></span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <Target className="h-4 w-4 text-green-400" />
                <span>Complete daily challenge: <strong>20 points</strong></span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-3 text-gray-300">
                <Award className="h-4 w-4 text-purple-400" />
                <span>Unlock achievements: <strong>10-300 points</strong></span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <Zap className="h-4 w-4 text-orange-400" />
                <span>Daily streak bonuses: <strong>15-250 points</strong></span>
              </div>
            </div>
          </div>
        </div>

        {/* Achievement Categories */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold gradient-text mb-3 flex items-center space-x-2">
            <Trophy className="h-4 w-4" />
            <span>Achievement Categories</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div className="bg-black/20 rounded-lg p-3 hover:bg-black/30 transition">
              <div className="flex items-center space-x-2 mb-1">
                <Brain className="h-4 w-4 text-blue-400" />
                <span className="text-sm text-gray-300">Useless Facts</span>
              </div>
              <p className="text-xs text-gray-400">Like useless facts to unlock</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 hover:bg-black/30 transition">
              <div className="flex items-center space-x-2 mb-1">
                <Heart className="h-4 w-4 text-red-400" />
                <span className="text-sm text-gray-300">Animal Facts</span>
              </div>
              <p className="text-xs text-gray-400">Like animal facts to earn</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 hover:bg-black/30 transition">
              <div className="flex items-center space-x-2 mb-1">
                <Flask className="h-4 w-4 text-indigo-400" />
                <span className="text-sm text-gray-300">Science Facts</span>
              </div>
              <p className="text-xs text-gray-400">Like science facts to unlock</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 hover:bg-black/30 transition">
              <div className="flex items-center space-x-2 mb-1">
                <Trophy className="h-4 w-4 text-yellow-400" />
                <span className="text-sm text-gray-300">Records</span>
              </div>
              <p className="text-xs text-gray-400">Like pointless records</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 hover:bg-black/30 transition">
              <div className="flex items-center space-x-2 mb-1">
                <Lightbulb className="h-4 w-4 text-amber-400" />
                <span className="text-sm text-gray-300">Inventions</span>
              </div>
              <p className="text-xs text-gray-400">Like useless inventions</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 hover:bg-black/30 transition">
              <div className="flex items-center space-x-2 mb-1">
                <Calculator className="h-4 w-4 text-cyan-400" />
                <span className="text-sm text-gray-300">Numbers</span>
              </div>
              <p className="text-xs text-gray-400">Like number facts to earn</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 hover:bg-black/30 transition">
              <div className="flex items-center space-x-2 mb-1">
                <Smile className="h-4 w-4 text-pink-400" />
                <span className="text-sm text-gray-300">Excuses</span>
              </div>
              <p className="text-xs text-gray-400">Like random excuses</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 hover:bg-black/30 transition">
              <div className="flex items-center space-x-2 mb-1">
                <Target className="h-4 w-4 text-green-400" />
                <span className="text-sm text-gray-300">Challenges</span>
              </div>
              <p className="text-xs text-gray-400">Complete daily challenges</p>
            </div>
          </div>
        </div>

        {/* Most Liked Today */}
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-2">Most Liked Today</h3>
          {topLikes.length > 0 && (
            <div className="space-y-2">
              {topLikes.slice(0, 3).map((like, index) => (
                <div
                  key={like.id}
                  onClick={() => setSelectedItem(like)}
                  className="flex items-center justify-between p-2 rounded-lg bg-black/20 hover:bg-black/30 transition cursor-pointer"
                >
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">
                      {index === 0 ? '🏆 Top' : `#${index + 1}`}
                    </span>
                    <span className="truncate text-sm text-gray-300">{like.content}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-red-400 ml-2">
                    <Heart className="h-4 w-4 fill-current" />
                    <span className="font-medium">{like.likes}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* View All Button */}
        <button
          onClick={() => setShowAchievements(true)}
          className="w-full gradient-bg text-white py-2 rounded-lg hover:opacity-90 transition flex items-center justify-center space-x-2 text-sm mt-6"
        >
          <Crown className="h-4 w-4" />
          <span>View All Achievements</span>
        </button>
      </div>

      {/* Modals */}
      {selectedItem && renderDetailModal(selectedItem)}
      {showAchievements && (
        <AchievementsModal
          achievements={achievements}
          userAchievements={userAchievements}
          onClose={() => setShowAchievements(false)}
        />
      )}
    </div>
  );
};

export default TopAchievement;